import { StrictMode } from "react";
import { createRoot } from "react-dom/client";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { RouterProvider } from "@tanstack/react-router";
import { router } from "./router";
import { I18nProvider } from "./lib/i18n";
import { ApiError } from "./lib/api";
import { DialogProvider, ToastProvider } from "./components/ui";
import "@fontsource-variable/geist";
import "@fontsource-variable/geist-mono";
import "@fontsource/ibm-plex-sans-arabic/arabic-400.css";
import "@fontsource/ibm-plex-sans-arabic/arabic-500.css";
import "@fontsource/ibm-plex-sans-arabic/arabic-600.css";
import "@fontsource/ibm-plex-sans-arabic/arabic-700.css";
import "./index.css";

// Keep marketing attribution (utm_*, ref) from the first landing URL for onboarding.
try {
  const p = new URLSearchParams(location.search);
  const utm = Object.fromEntries([...p].filter(([k]) => k.startsWith("utm_")));
  if (Object.keys(utm).length || p.get("ref"))
    sessionStorage.setItem("sooquna.attribution", JSON.stringify({ utm, ref: p.get("ref") ?? "", landing_url: location.href }));
  // The website's signup links (web/marketing src/lib/links.ts): plan and
  // type preselect onboarding and the first store; lang the admin language
  // unless one was already chosen.
  if (p.get("plan") || p.get("type"))
    sessionStorage.setItem("sooquna.signup", JSON.stringify({ plan: p.get("plan") ?? "", type: p.get("type") ?? "" }));
  const lang = p.get("lang");
  if (lang && !localStorage.getItem("sooquna.locale")) localStorage.setItem("sooquna.locale", lang);
} catch { /* storage unavailable */ }

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 30_000,
      retry: (n, err) => !(err instanceof ApiError && err.status < 500) && n < 2,
      refetchOnWindowFocus: true,
    },
  },
});

createRoot(document.getElementById("root")!).render(
  <StrictMode>
    <I18nProvider>
      <QueryClientProvider client={queryClient}>
        <ToastProvider>
          <DialogProvider>
            <RouterProvider router={router} />
          </DialogProvider>
        </ToastProvider>
      </QueryClientProvider>
    </I18nProvider>
  </StrictMode>,
);
